/**
 * Top-bar controls: office selector, freshness badge, theme toggle,
 * notifications and the user menu.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { PRODUCT } from '@velora/contracts/product';
import type { OfficeFreshness } from '@velora/contracts/shell';
import { getShellFreshness } from '@velora/sdk';
import {
  Avatar,
  Badge,
  Button,
  connectorFreshness,
  DEFAULT_FRESHNESS,
  FreshnessBadge,
  IconButton,
  Menu,
  OfficeSwitcher,
  Popover,
  relativeAge,
  type FreshnessThresholds,
  type MenuEntry,
  type OfficeInfo,
} from '@velora/ui';
import { Bell, CheckCheck, Moon, Sun } from 'lucide-react';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { clientFor } from '../api.js';
import { ADD_OFFICE_PATH, useCanAddOffice } from '../identity/access.js';
import { useLiveStatus } from '../live/LiveProvider.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { usePrefs } from '../prefs/prefs.js';
import { usePrivacy } from '../privacy/privacy.js';
import { createRegistry } from '../registry/store.js';
import { usePermission, useSession } from '../session/session.js';
import { notifications, type ShellNotification } from '../notifications/store.js';
import { summaryText, SyncProgressView, SyncRing } from '../sync-progress/SyncProgressView.js';
import { useSyncProgress } from '../sync-progress/useSyncProgress.js';

/** Settings > Sync status (modules/sync-status). */
export const SYNC_STATUS_PATH = '/settings/sync-status';

/* ---------- office selector ---------- */

const PMS_LABEL: Record<string, string> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

export function OfficeControl({
  allowAll,
  open,
  onOpenChange,
}: {
  allowAll: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { offices, scope, setScope } = useOfficeScope();
  const canAddOffice = useCanAddOffice();
  const navigate = useNavigate();
  const infos = useMemo<OfficeInfo[]>(
    () =>
      offices.map((o) => ({
        id: o.id,
        name: o.name,
        shortName: o.shortName,
        colorIndex: o.colorIndex,
        detail: o.pmsSystem
          ? `${PMS_LABEL[o.pmsSystem] ?? o.pmsSystem} via PulseSync`
          : 'No PMS linked',
      })),
    [offices],
  );
  return (
    <OfficeSwitcher
      offices={infos}
      value={scope}
      onChange={setScope}
      allowAll={allowAll}
      {...(canAddOffice
        ? {
            onAddOffice: () =>
              void navigate({
                to: ADD_OFFICE_PATH,
                search: (prev: Record<string, unknown>) => prev,
              } as never),
          }
        : {})}
      open={open}
      onOpenChange={onOpenChange}
      shortcut={['L']}
    />
  );
}

/* ---------- freshness ---------- */

export const FRESHNESS_KEY = ['shell', 'freshness'] as const;
export const FRESHNESS_POLL_MS = 60_000;

export function useFreshness() {
  const { param, locationIds } = useOfficeScope();
  return useQuery({
    queryKey: [...FRESHNESS_KEY, { office: param, live: ['sync_run', 'sync_cursor', 'pms_link'] }],
    queryFn: ({ signal }) => getShellFreshness(clientFor(param), { signal }),
    // Connector cycles are minutes apart and the chip judges in intervals of
    // them: once a minute is enough for every open tab (was 30 s, 2026-10-21).
    refetchInterval: FRESHNESS_POLL_MS,
    refetchIntervalInBackground: false,
    enabled: locationIds.length > 0,
  });
}

/**
 * Oldest sync across linked offices: the badge reflects the weakest link.
 * `intervalSec` is that office's connector interval (PulseSync), which sets
 * the chip's amber/red thresholds (`connectorFreshness`); null keeps the
 * default thresholds.
 */
export function combineFreshness(rows: readonly OfficeFreshness[]): {
  linked: OfficeFreshness[];
  oldest: string | null;
  neverSynced: number;
  intervalSec: number | null;
} {
  const linked = rows.filter((r) => r.pmsSystem !== null);
  let oldest: string | null = null;
  let intervalSec: number | null = null;
  let never = 0;
  for (const r of linked) {
    if (r.lastSyncedAt === null) never += 1;
    else if (oldest === null || r.lastSyncedAt < oldest) {
      oldest = r.lastSyncedAt;
      intervalSec = r.intervalSec ?? null;
    }
  }
  return {
    linked,
    oldest: never > 0 ? null : oldest,
    neverSynced: never,
    intervalSec: never > 0 ? null : intervalSec,
  };
}

/** The chip's thresholds: the connector-interval rule for a PulseSync office (pure). */
export function chipThresholds(intervalSec: number | null): FreshnessThresholds {
  return intervalSec === null ? DEFAULT_FRESHNESS : connectorFreshness(intervalSec);
}

/**
 * The top-bar sync chip ("Synced 1m ago"). While a connector cycle or a
 * backfill is running it carries a small progress ring; clicking it opens
 * the per-office breakdown (polled every 12 s while open), which links to
 * Settings > Sync status.
 */
export function FreshnessControl() {
  const { offices } = useOfficeScope();
  // A late sync is red only for people who can act on it (PulseSync is in
  // Settings): everyone else sees a neutral chip with the same age text
  // (MODULE-AUDIT X5).
  const canAct = usePermission('settings.manage');
  const navigate = useNavigate();
  const fresh = useFreshness();
  const live = useLiveStatus();
  const [open, setOpen] = useState(false);
  const progress = useSyncProgress({ open });
  const now = useNow(15_000);
  if (!fresh.data) return null;
  const { linked, oldest, neverSynced, intervalSec } = combineFreshness(fresh.data.offices);
  const busy = progress.data?.active ?? false;
  const hasBackfills = progress.data?.offices.some((o) => o.backfills.length > 0) ?? false;
  if (linked.length === 0 && !hasBackfills) return null;
  const names = new Map(offices.map((o) => [o.id, o.name]));
  const source =
    linked.length === 1
      ? `${PMS_LABEL[linked[0]?.pmsSystem ?? ''] ?? 'PMS'} via PulseSync (${names.get(linked[0]?.locationId ?? '') ?? 'office'})`
      : `${linked.length} offices via PulseSync`;
  const detailParts = [
    neverSynced > 0 ? `${neverSynced} office${neverSynced > 1 ? 's' : ''} not synced yet` : null,
    linked.some((l) => l.writesPaused) ? 'write-back paused' : null,
    live.status === 'open'
      ? 'live updates on'
      : live.status === 'idle'
        ? null
        : 'live updates reconnecting',
  ].filter(Boolean);
  const pct = progress.data?.overall?.percent ?? null;
  const summary = progress.data ? summaryText(progress.data) : null;
  const ringLabel = `${summary ?? 'Syncing'}${pct !== null ? `, ${pct}% overall` : ''}`;
  if (busy) detailParts.push(ringLabel);
  const lastText = oldest === null ? 'never synced' : `last sync ${relativeAge(oldest, now)}`;
  const tip = `${source}: ${lastText}${detailParts.length > 0 ? `. ${detailParts.join(', ')}` : ''}. Show sync progress`;
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      label="Sync progress"
      className="syncp-pop"
      trigger={
        <FreshnessBadge
          lastSyncedAt={oldest}
          source={source}
          thresholds={chipThresholds(intervalSec)}
          tooltip={false}
          quiet={!canAct}
          aria-label={tip}
          aria-haspopup="dialog"
          data-busy={busy ? '' : undefined}
          adornment={busy ? <SyncRing percent={pct} /> : null}
          {...(detailParts.length > 0 ? { detail: detailParts.join(', ') } : {})}
        />
      }
    >
      <div className="syncp-pop__head">
        <h2>Sync progress</h2>
        <p className="syncp-muted" aria-live="polite">
          {summary ?? 'Checking...'}
          {pct !== null && busy ? ` · ${pct}% of what is filling` : ''}
        </p>
      </div>
      {progress.data ? (
        <SyncProgressView data={progress.data} names={names} now={now} compact />
      ) : progress.isError ? (
        <p className="syncp-error" role="alert">
          Could not load sync progress.
        </p>
      ) : (
        <p className="syncp-muted">Loading...</p>
      )}
      <div className="syncp-pop__foot">
        <span className="syncp-muted">Updates every 12 s while open</span>
        <a
          className="syncp-link"
          href={SYNC_STATUS_PATH}
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            void navigate({
              to: SYNC_STATUS_PATH,
              search: (prev: Record<string, unknown>) => prev,
            } as never);
          }}
        >
          Open Sync status
        </a>
      </div>
    </Popover>
  );
}

/* ---------- theme ---------- */

export function ThemeToggle() {
  const { mode, setMode } = usePrefs();
  const next = mode === 'dark' ? 'light' : 'dark';
  return (
    <IconButton
      label={`Switch to ${next} theme`}
      icon={mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      variant="ghost"
      onClick={() => setMode(next)}
    />
  );
}

/* ---------- notifications ---------- */

function useNotifications() {
  const items = useSyncExternalStore(notifications.subscribe, notifications.get, notifications.get);
  return { items, unread: items.filter((i) => !i.read).length };
}

function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

function NotificationRow({
  n,
  now,
  onOpen,
}: {
  n: ShellNotification;
  now: number;
  onOpen: (href: string) => void;
}) {
  const tone =
    n.tone === 'danger'
      ? 'danger'
      : n.tone === 'warning'
        ? 'warning'
        : n.tone === 'success'
          ? 'success'
          : 'info';
  const body = (
    <>
      <span className="shell-notif__dot" data-tone={tone} aria-hidden />
      <span className="shell-notif__text">
        <span className="shell-notif__title">{n.title}</span>
        {n.body ? <span className="shell-notif__body">{n.body}</span> : null}
        <span className="shell-notif__time">{relativeAge(n.at, now)}</span>
      </span>
    </>
  );
  return (
    <li className="shell-notif" data-unread={n.read ? undefined : ''}>
      {n.href ? (
        <button type="button" className="shell-notif__main" onClick={() => onOpen(n.href ?? '/')}>
          {body}
        </button>
      ) : (
        <div className="shell-notif__main">{body}</div>
      )}
    </li>
  );
}

export function NotificationsMenu() {
  const { items, unread } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const now = useNow(30_000);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) notifications.markAllRead();
      }}
      align="end"
      padded={false}
      label="Notifications"
      className="shell-notifs"
      trigger={
        <button
          type="button"
          className="shell-iconbtn shell-bell"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Bell size={16} aria-hidden />
          {unread > 0 ? (
            <span className="shell-iconbtn__count vel-num" aria-hidden>
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
      }
    >
      <div className="shell-notifs__head">
        <span>Notifications</span>
        {items.length > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<CheckCheck size={14} />}
            onClick={() => notifications.clear()}
          >
            Clear
          </Button>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="shell-notifs__empty">You are all caught up.</p>
      ) : (
        <ul className="shell-notifs__list">
          {items.map((n) => (
            <NotificationRow
              key={n.id}
              n={n}
              now={now}
              onOpen={(href) => {
                setOpen(false);
                void navigate({ to: href });
              }}
            />
          ))}
        </ul>
      )}
    </Popover>
  );
}

/* ---------- user menu ---------- */

export interface UserMenuItem {
  /** Unique id; also sorts the item (lower `order` first). */
  id: string;
  label: string;
  order?: number;
  tone?: 'danger';
  /**
   * 'top' lists the item first, under the name (My Hub); its badge also
   * shows on the avatar. Default: after the shell's own entries.
   */
  placement?: 'top';
  /** Count after the label (0 or undefined hides it). */
  badge?: number;
  /** Wording for the badge, e.g. "2 overdue items in My Hub". */
  badgeLabel?: string;
  onSelect: () => void;
}

/** Modules add entries (identity adds "Sign out", "Security") at runtime. */
export const userMenuItems = createRegistry<UserMenuItem>();

export function registerUserMenuItem(item: UserMenuItem): () => void {
  return userMenuItems.register(item);
}

export function UserMenu({ onShortcuts }: { onShortcuts: () => void }) {
  const session = useSession();
  const navigate = useNavigate();
  const { mode, setMode, density, navCollapsed, set } = usePrefs();
  const privacy = usePrivacy();
  const extra = useSyncExternalStore(userMenuItems.subscribe, userMenuItems.get, userMenuItems.get);
  const name = session.user.displayName;
  const byOrder = (a: UserMenuItem, b: UserMenuItem) => (a.order ?? 100) - (b.order ?? 100);
  const toEntry = (i: UserMenuItem): MenuEntry => ({
    id: i.id,
    label: i.label,
    onSelect: i.onSelect,
    ...(i.tone ? { tone: i.tone } : {}),
    ...(i.badge ? { badge: i.badge } : {}),
    ...(i.badge && i.badgeLabel ? { title: i.badgeLabel } : {}),
  });
  const top = extra.filter((i) => i.placement === 'top').sort(byOrder);
  const rest = extra.filter((i) => i.placement !== 'top').sort(byOrder);
  const flagged = top.filter((i) => (i.badge ?? 0) > 0);
  const avatarCount = flagged.reduce((n, i) => n + (i.badge ?? 0), 0);
  const avatarLabel = flagged.map((i) => i.badgeLabel ?? `${i.badge} in ${i.label}`).join(', ');
  const entries: MenuEntry[] = [
    { type: 'label', id: 'who', label: `${name}, ${session.practice.name}` },
    { type: 'separator', id: 's0' },
    ...top.map(toEntry),
    ...(top.length > 0 ? [{ type: 'separator' as const, id: 's0b' }] : []),
    {
      type: 'checkbox',
      id: 'theme',
      label: 'Dark theme',
      checked: mode === 'dark',
      onCheckedChange: (c) => setMode(c ? 'dark' : 'light'),
    },
    {
      type: 'checkbox',
      id: 'compact',
      label: 'Compact density',
      checked: density === 'compact',
      onCheckedChange: (c) => set({ density: c ? 'compact' : 'comfortable' }),
    },
    {
      type: 'checkbox',
      id: 'privacy',
      label: 'Privacy mode (Shift+P)',
      checked: privacy.on,
      onCheckedChange: (c) => privacy.setOn(c),
    },
    {
      type: 'checkbox',
      id: 'nav',
      label: 'Collapse navigation',
      checked: navCollapsed,
      onCheckedChange: (c) => set({ navCollapsed: c }),
    },
    { type: 'separator', id: 's1' },
    // The Store's one everyday entry (owner-research-marketplace.md section 4).
    {
      id: 'marketplace',
      label: 'Marketplace',
      onSelect: () => void navigate({ to: '/marketplace' }),
    },
    { id: 'shortcuts', label: 'Keyboard shortcuts', shortcut: ['?'], onSelect: onShortcuts },
    ...(rest.length > 0 ? [{ type: 'separator' as const, id: 's2' }] : []),
    ...rest.map(toEntry),
  ];
  return (
    <Menu
      align="end"
      items={entries}
      trigger={
        <button
          type="button"
          className="shell-user"
          aria-label={`Account: ${name}${avatarCount > 0 ? `. ${avatarLabel}` : ''}`}
        >
          <span className="shell-user__avatar">
            <Avatar name={name} size={30} />
            {avatarCount > 0 ? (
              <span className="shell-user__badge" aria-hidden>
                {avatarCount > 9 ? '9+' : avatarCount}
              </span>
            ) : null}
          </span>
          <span className="shell-user__meta">
            <strong>{name}</strong>
            <span>
              {session.user.impersonatedBy
                ? `Support: ${session.user.impersonatedBy}`
                : session.practice.name}
            </span>
          </span>
        </button>
      }
    />
  );
}

export function DemoBadge() {
  return (
    <Badge
      tone="warning"
      variant="outline"
      title={`Synthetic demo data. Nothing here is real. ${PRODUCT.shortName} demo mode.`}
    >
      Demo data
    </Badge>
  );
}
